import { useApolloClient } from '@apollo/client'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useErrorInfoModal } from '@features/modal'

import {
  CustomerActivePaymentMethodsDocument,
  type CustomerActivePaymentMethodsQuery,
  useChooseDefaultPaymentMethodMutation,
  useChoosePaymentMethodForOrderMutation,
  useCustomerActivePaymentMethodsQuery,
  useRemovePaymentMethodMutation,
} from '@shared/generated'

export function useManagePaymentMethods () {
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const { data, loading } = useCustomerActivePaymentMethodsQuery({
    fetchPolicy: 'cache-and-network',
  })
  const [ chooseDefault ] = useChooseDefaultPaymentMethodMutation()
  const [ choosePaymentMethodForOrder ] = useChoosePaymentMethodForOrderMutation()
  const [ removeMethod ] = useRemovePaymentMethodMutation()
  const client = useApolloClient()

  const paymentMethods = data?.customerActivePaymentMethods ?? []

  const defaultCard = paymentMethods.find((m) => m?.isDefault) ?? null

  const chooseForOrder = useCallback(async (orderId: string, paymentMethodId: string) => {
    try {
      const result = await choosePaymentMethodForOrder({
        variables: {
          orderId,
          paymentMethodId,
          markAsDefault: true
        }
      })

      const { paymentMethod = null } = result.data?.choosePaymentMethodForOrder ?? {}

      if (paymentMethod && paymentMethod.isDefault) {
        const existing = client.cache.readQuery<CustomerActivePaymentMethodsQuery>({
          query: CustomerActivePaymentMethodsDocument,
        })

        if (existing?.customerActivePaymentMethods) {
          client.cache.writeQuery<CustomerActivePaymentMethodsQuery>({
            query: CustomerActivePaymentMethodsDocument,
            data: {
              __typename: 'Query',
              customerActivePaymentMethods:
                existing.customerActivePaymentMethods.map((method) => ({
                  ...method,
                  isDefault: method?.id === paymentMethod.id,
                  __typename: method?.__typename ?? 'CustomerPaymentMethod',
                })),
            },
          })
        }
      }
    }
    catch (error) {
      console.error('Error choosing order payment method: ', error)
      openErrorInfoModal({
        description: te('choose_default_payment_method'),
      })
    }

  }, [ choosePaymentMethodForOrder, client.cache, openErrorInfoModal, te ])


  const handleDefault = useCallback(
    async (id: string) => {
      const existing =
        client.cache.readQuery<CustomerActivePaymentMethodsQuery>({
          query: CustomerActivePaymentMethodsDocument,
        })

      const alreadyDefault = existing?.customerActivePaymentMethods?.some(
        (method) => method?.id === id && method?.isDefault,
      )

      if (alreadyDefault) return

      try {
        await chooseDefault({
          variables: { paymentMethodId: id },
        })

        if (!existing?.customerActivePaymentMethods) return

        client.cache.writeQuery<CustomerActivePaymentMethodsQuery>({
          query: CustomerActivePaymentMethodsDocument,
          data: {
            __typename: 'Query',
            customerActivePaymentMethods:
              existing.customerActivePaymentMethods.map((method) => ({
                ...method,
                isDefault: method?.id === id,
                __typename: method?.__typename ?? 'CustomerPaymentMethod',
              })),
          },
        })
      }
      catch (error) {
        console.error('Error choosing default payment method:', error)
        openErrorInfoModal({
          description: te('choose_default_payment_method'),
        })
      }
    },
    [ chooseDefault, client.cache ],
  )

  const handleRemove = useCallback(
    async (id: string) => {
      try {
        await removeMethod({
          variables: { removePaymentMethodId: id },
        })

        const existing =
          client.cache.readQuery<CustomerActivePaymentMethodsQuery>({
            query: CustomerActivePaymentMethodsDocument,
          })

        if (!existing) return

        client.cache.writeQuery<CustomerActivePaymentMethodsQuery>({
          query: CustomerActivePaymentMethodsDocument,
          data: {
            __typename: 'Query',
            customerActivePaymentMethods: existing.customerActivePaymentMethods
              ?.filter((method) => method?.id !== id)
              .map((method) => ({
                ...method,
                __typename: method?.__typename ?? 'CustomerPaymentMethod',
              })),
          },
        })
      }
      catch (error) {
        console.error('Error removing payment method:', error)
        openErrorInfoModal({
          description: te('remove_payment_method'),
        })
      }
    },
    [ removeMethod, client.cache ],
  )

  return {
    paymentMethods,
    defaultCard,
    loading,
    handleDefault,
    handleRemove,
    chooseForOrder
  }
}
